/**
 * 모바일 관리자 — 제재 관리 (기사 상세 「제재」 블록의 [제재 관리]).
 *
 * 서버는 데스크탑 정본을 그대로 쓴다 (`Admin\PenaltyController` → `/ma/ajax/member/{n}/penalt*`).
 * 판정은 전부 서버가 내려주는 플래그를 따른다 — 해제 가능(`canRelease`)·수정 가능
 * (`canEditReason`)·소속자 읽기전용(`owner.isSosok`). 화면이 따로 판단하면 목록엔 보이는데
 * 눌러보면 막히는 버튼이 생긴다.
 *
 * **화면만 폰 것이다.** PC 는 처벌 1건 = 1행인 표(DataTables)인데 폰에서는 열 여섯을 못 읽는다
 * → **사건 하나가 카드**고 그 안에 처벌이 줄로 쌓인다. 같은 사건이 이어질 때 접는 `〃` 도
 * 필요 없다(묶여 있으므로).
 *
 * 부과·수정·해제·삭제 창은 목록 위에 겹치지 않고 **같은 모달을 갈아끼운다**
 * (`Sites/MobileAdmin.modal` 의 stacked 경로 — 히스토리 항목을 물려받는다).
 * 그래서 창의 [취소]는 창을 닫지 않고 **목록으로 되돌린다**.
 *
 * 폰에는 hover 가 없어 PC 의 툴팁(`data-tip`)으로 말하던 것들(차단없음·해제사유·근거)은
 * 전부 글로 적는다.
 *
 * 설계: .ai/domains/penalty.md
 */

/** 상태 필터 — PC 모달의 셀렉트와 같은 값이다 (기본 집행중) */
const STATES = [['active', '집행중'], ['', '전체'], ['off', '해제됨']];

/** 근거 슬롯(rel_table) → 사람이 읽는 이름. PC srcTag 와 같은 목록 */
const SRC = {
    counsel:    '상담',
    order:      '환원오더',
    ring:       '한 패',
    relation:   '수발주제한',
    collusion:  '부정발급 감시',
    cfg_baljoo: '구 명단',
    cfg_cash:   '구 명단',
};

export default class PenaltySheet {
    /**
     * @param {object}   o
     * @param {number}   o.mbNo     회원번호
     * @param {string}   [o.name]   창 제목에 쓸 회원명
     * @param {object}   [o.sanc]   기사 상세가 이미 받아둔 sanctions (요약 + list + charges·kinds·owner)
     * @param {Function} [o.onChange] 원장이 바뀌면 호출 — 부른 쪽(기사 상세)을 다시 그리라고
     */
    constructor(o = {}) {
        this.mbNo     = parseInt(o.mbNo, 10) || 0;
        this.name     = o.name || '';
        this.sanc     = o.sanc || null;
        this.onChange = (typeof o.onChange === 'function') ? o.onChange : null;
        this.state    = 'active';
    }

    get M() { return $j.Sites.MobileAdmin; }

    open() {
        if (!this.mbNo) return;
        // 시트가 넘겨준 값이 있으면 그대로 연다 — 상세 응답에 목록·선택지가 이미 실려 있다
        // (MemberEditFormService::sanctions). 없으면(시트 밖에서 열린 경우) 한 번 받아온다.
        if (this.sanc && this.sanc.list && this.sanc.charges) return this.openList();
        this.fetch(() => this.openList());
    }

    /** 이력 + 요약 + 선택지 — 한 응답에 다 온다 (PenaltyController::getList) */
    fetch(after) {
        $.getJSON(`/ma/ajax/member/${this.mbNo}/penalties`)
            .done((r) => {
                if (!r || r.result === false) return this.toast('제재 내역을 불러오지 못했습니다');
                this.merge(r);
                after();
            })
            .fail(() => this.toast('제재 내역을 불러오지 못했습니다'));
    }

    /**
     * 응답을 화면 상태에 반영한다. 목록과 요약은 **항상 서버 응답에서만** 온다 —
     * 부과·해제 뒤 화면을 직접 손대면 원장과 어긋난다.
     * 쓰기 응답(add·update·release·delete)에는 선택지·소유자 정보가 없어 있던 값을 지킨다.
     */
    merge(r) {
        this.sanc = Object.assign({}, this.sanc, r.summary || {}, { list: r.list || [] });
        if (r.charges) this.sanc.charges = r.charges;
        if (r.kinds)   this.sanc.kinds   = r.kinds;
        if (r.owner)   this.sanc.owner   = r.owner;
    }

    // ───────── 목록 ─────────

    openList() {
        const m = this.M.modal({
            title:    `제재 관리${this.name ? ` — ${this.esc(this.name)}` : ''}`,
            hideOk:   true,
            bodyHtml: this.listHtml(),
        });
        this.bindList(m.bodyEl);
    }

    listHtml() {
        const s    = this.sanc || {};
        const ro   = this.isSosok();
        const rows = (s.list || []).filter((p) => this.match(p));

        const states = STATES.map(([v, label]) =>
            `<button type="button" class="ma-chip${v === this.state ? ' is-active' : ''}" data-penstate="${v}">${label}</button>`
        ).join('');

        return `<div class="ma-pen">
            <div class="ma-pen-sum">${this.chips(s)}</div>
            <p class="ma-pen-prior">${this.priorHtml(s)}</p>
            ${ro
                ? `<p class="ma-md-sanction">제재는 소유자에게 매깁니다. 부과·해제는 소유자(${this.esc(this.ownerLabel())}) 화면에서 하세요.
                   아래는 그 소유자의 제재입니다.</p>`
                : '<div class="ma-act-btns"><button type="button" class="ma-ab-btn primary wide" data-pen="add">＋ 제재부과</button></div>'}
            <div class="ma-pen-states">${states}</div>
            ${rows.length ? this.caseCards(rows) : `<div class="ma-sh-note">${this.state === 'active' ? '집행중인 제재가 없습니다' : '제재 이력이 없습니다'}</div>`}
            <p class="ma-pen-foot">환원페널티 적용중 ${this.n(s.whanActive)}건 · 누적 ${this.n(s.whanTotal)}건${s.whanLast ? ` · 최근 ${this.esc(s.whanLast)}` : ''}
                <span>환원은 변제(수금완료)로만 풀립니다.</span></p>
        </div>`;
    }

    bindList(box) {
        box.querySelectorAll('[data-penstate]').forEach((b) => {
            b.onclick = () => { this.state = b.dataset.penstate; this.openList(); };
        });
        box.querySelectorAll('[data-pen]').forEach((b) => {
            const num = parseInt(b.dataset.num, 10) || 0;
            b.onclick = () => this.onPen(b.dataset.pen, num);
        });
    }

    onPen(act, num) {
        if (act === 'add')     return this.openForm(null);
        if (act === 'edit') {
            const row = this.rowOfCase(num);
            return row ? this.openForm(row) : this.toast('수정할 사건을 찾을 수 없습니다');
        }
        if (act === 'release') return this.releaseForm(this.rowOf(num));
        if (act === 'delete')  return this.deleteForm(this.rowOf(num));
    }

    /** 상태 필터 — 값은 PC 와 같다 (빈값 = 전체) */
    match(p) {
        if (!this.state) return true;
        return this.state === 'active' ? !!p.active : !p.active;
    }

    /** 평탄한 처벌 목록을 **사건 단위**로 묶는다 (서버 정렬을 그대로 따른다) */
    caseCards(rows) {
        const cases = [];
        let cur = null;
        rows.forEach((p) => {
            if (!cur || cur.num !== p.caseNum) { cur = { num: p.caseNum, head: p, list: [] }; cases.push(cur); }
            cur.list.push(p);
        });

        return cases.map((c) => this.caseCard(c)).join('');
    }

    caseCard(c) {
        const h   = c.head;
        const ban = c.list.some((p) => p.active && p.level >= 2);
        const src = SRC[h.relTable]
            ? ` <span class="ma-pen-src">${this.esc(SRC[h.relTable])}${h.relId ? ` #${this.n(h.relId)}` : ''}</span>`
            : '';

        return `<div class="ma-pen-case${ban ? ' is-ban' : ''}">
            <div class="ma-pen-hd">
                <b>${this.esc(h.chargeTitle)}</b>
                <span>${this.esc(h.openedAt || '날짜없음')} · ${this.esc(h.manageName || '시스템')}</span>
                ${this.editBtn(h)}
            </div>
            <p class="ma-pen-reason">${this.esc(h.reason) || '사유 미기재'}${src}</p>
            ${c.list.map((p) => this.punishRow(p)).join('')}
        </div>`;
    }

    /**
     * 사건 수정(✎) — 죄목·처벌·사유를 한 창에서 고친다.
     * 조건은 PC 와 같다: 환원(자동 죄목)·쌍 제한 집행중 사건은 붙이지 않고, 소속자 화면은 읽기전용.
     */
    editBtn(h) {
        if (this.isSosok() || !h.canEditReason) return '';
        if (h.active && !h.canRelease) return '';   // 쌍 제한이 집행중인 사건

        return `<button type="button" class="ma-pen-edit" data-pen="edit" data-num="${this.n(h.caseNum)}"
                    aria-label="제재 수정"><i class="fas fa-pen"></i></button>`;
    }

    /** 처벌 한 줄 — 왼쪽에 무슨 처벌인지, 오른쪽에 지금 어떤 상태인지 */
    punishRow(p) {
        const mates = (p.mates || []).map((m) => this.esc(m.memName) || `#${this.n(m.mbNo)}`);
        // 경고는 막는 게 없다 — 그 한 마디가 "경고인데 왜 발주가 되지?" 를 막는다
        const note = mates.length ? `↔ ${mates.join(' · ')}` : (p.blocks ? '' : '차단없음');

        return `<div class="ma-pen-row">
            <span class="ma-pen-kind ${p.level >= 2 ? 'is-ban' : 'is-warn'}">${this.esc(p.punishTitle)}</span>
            ${note ? `<em>${note}</em>` : ''}
            <span class="ma-pen-state">${this.stateHtml(p)}</span>
        </div>`;
    }

    stateHtml(p) {
        if (p.canRelease) {
            return this.isSosok()
                ? '<i>소유자 화면에서</i>'
                : '<button type="button" class="ma-pen-btn" data-pen="release" data-num="' + this.n(p.num) + '">해제</button>';
        }
        // 상대가 있는 처벌·시스템이 푸는 처벌 — 왜 여기서 못 푸는지 적는다
        if (p.active && p.kind === 'PAIR_BAN')    return '<i>수발주제한 화면에서</i>';
        if (p.active && p.kind === 'MSIX_RECALL') return '<i>회수 완료</i>';
        if (p.active) return '<i>변제하면 자동해제</i>';

        const memo = this.esc(p.releaseMemo) || '사유 미기재';
        const by   = p.releaseName ? ` · ${this.esc(p.releaseName)}` : '';
        const del  = this.isSosok() ? ''
            : `<button type="button" class="ma-pen-btn is-del" data-pen="delete" data-num="${this.n(p.num)}">삭제</button>`;

        return `<i>해제 ${this.esc(p.releasedAt)} — ${memo}${by}</i>${del}`;
    }

    /** 집행중인 처벌 배지 — '지금 무엇에 걸려 있나' 만 말한다 (횟수는 아래 전과 줄이 말한다) */
    chips(s) {
        const out = (s.active || []).map((a) =>
            `<span class="ma-pen-kind ${a.level >= 2 ? 'is-ban' : 'is-warn'}">${this.esc(a.title)}${+a.cnt > 1 ? ` ${this.n(a.cnt)}` : ''}</span>`
        );

        return out.length ? out.join(' ') : '<span class="ma-pen-kind is-none">집행중인 처벌 없음</span>';
    }

    /** 전과 = 해제된 것까지 포함한 **사건 수**. 요주의는 경고 누적 표시일 뿐 처벌이 아니다 */
    priorHtml(s) {
        if (!+s.priorCnt) return '전과 없음';

        const by = (s.priorBy || []).map((p) => `${this.esc(p.title)} ${this.n(p.cnt)}`).join(' · ');

        return `전과 <b>${this.n(s.priorCnt)}건</b>${by ? ` · ${by}` : ''}`
            + (s.isWatch ? ` <span class="ma-pen-watch">요주의</span>` : '')
            + (s.lastAt ? ` · 최근 ${this.esc(s.lastAt)}` : '');
    }

    // ───────── 부과 · 수정 (같은 창) ─────────

    /**
     * 사건 폼 — **부과와 수정이 같은 창**이다 (PC 와 같은 규칙).
     * 셋(죄목·처벌·사유)은 한 사건의 세 면이라 따로 저장하면 그 사이에 사건이 반만 바뀐 채 남는다.
     *
     * @param {object|null} row 수정할 사건의 행 (null = 새 부과)
     */
    openForm(row) {
        if (this.isSosok()) return this.toast(`제재는 소유자(${this.ownerLabel()}) 화면에서 합니다`);

        const s    = this.sanc || {};
        const edit = !!row;

        // 지금 집행중인 처벌 — 무엇을 내렸는지(해제 사유가 필요한지)를 이걸로 가른다
        const on = edit
            ? (s.list || []).filter((r) => r.caseNum === row.caseNum && r.active).map((r) => r.kind)
            : [];

        const charges = (s.charges || []).map((c) =>
            `<option value="${this.esc(c.charge)}" data-desc="${this.esc(c.desc)}"${edit && c.charge === row.charge ? ' selected' : ''}>${this.esc(c.title)}</option>`
        ).join('');
        const kinds = (s.kinds || []).map((k) =>
            `<label class="ma-pen-check"><input type="checkbox" class="js-pen-kind" value="${this.esc(k.kind)}"${on.indexOf(k.kind) !== -1 ? ' checked' : ''}> ${this.esc(k.title)}</label>`
        ).join('');

        const m = this.formModal({
            title:   edit ? '제재 수정' : '제재 부과',
            okLabel: edit ? '저장' : '부과',
            okCls:   'danger',
            bodyHtml: `${edit ? '<p class="ma-mdl-help">내리는 처벌은 지워지지 않고 <b>해제 이력</b>으로 남습니다.</p>' : ''}
                <label class="ma-mdl-lb">죄목</label>
                <select class="ma-mdl-in" id="maPenCharge">${charges}</select>
                <p class="ma-mdl-help" id="maPenDesc"></p>
                <label class="ma-mdl-lb">처벌</label>
                <div class="ma-pen-checks">${kinds}</div>
                <label class="ma-mdl-lb">사유</label>
                <textarea class="ma-mdl-ta" id="maPenReason" maxlength="500" placeholder="오더번호·정황 등">${this.esc(edit ? row.reason : '')}</textarea>
                ${edit ? `<label class="ma-mdl-lb">해제 사유</label>
                    <input class="ma-mdl-in" id="maPenOffMemo" placeholder="처벌을 내릴 때만 — 내리지 않으면 비워둡니다">` : ''}`,
            onOk: (b) => {
                const charge = b.querySelector('#maPenCharge').value;
                const reason = (b.querySelector('#maPenReason').value || '').trim();
                const memo   = ((b.querySelector('#maPenOffMemo') || {}).value || '').trim();
                const picked = Array.prototype.map.call(b.querySelectorAll('.js-pen-kind:checked'), (el) => el.value);

                if (!charge)        return this.toast('죄목을 선택해주세요');
                if (!picked.length) return this.toast('처벌을 하나 이상 선택해주세요');
                if (!reason)        return this.toast('사유를 입력해주세요');
                // 내리는 처벌이 있으면 해제 사유를 받는다 — 목록의 [해제] 와 같은 이력이 남아야 한다
                if (on.some((k) => picked.indexOf(k) === -1) && !memo) return this.toast('내리는 처벌이 있으면 해제 사유를 입력해주세요');

                return edit
                    ? this.save('update', { caseNum: row.caseNum, charge, reason, kinds: picked, memo })
                    : this.save('add', { charge, reason, kinds: picked });
            },
        });

        // 죄목 설명 + (부과일 때만) 기본 체크.
        // **수정에서는 체크를 건드리지 않는다** — 죄목을 바꿨다고 집행중인 처벌을 조용히
        // 갈아치우면 의도하지 않은 해제·부과가 섞인다.
        // 부과의 기본값은 죄목과 무관하게 **경고 고정**이다 (PC drawDesc 와 같은 규칙) —
        // 죄목별 기본값이 수주금지 같은 센 처벌을 자동으로 얹으면 그대로 부과하기 쉽다.
        const sel  = m.bodyEl.querySelector('#maPenCharge');
        const draw = () => {
            const opt = sel.options[sel.selectedIndex];
            m.bodyEl.querySelector('#maPenDesc').textContent = (opt && opt.dataset.desc) || '';
            if (edit) return;
            m.bodyEl.querySelectorAll('.js-pen-kind').forEach((el) => { el.checked = el.value === 'WARN'; });
        };
        sel.onchange = draw;
        draw();
    }

    // ───────── 해제 · 삭제 ─────────

    releaseForm(p) {
        if (!p) return this.toast('해제할 제재를 찾을 수 없습니다');

        this.formModal({
            title: '제재 해제', okLabel: '해제', okCls: 'danger',
            // 행을 지우지 않는다는 사실을 먼저 말한다 — 해제가 기록을 없애는 일로 읽히면 망설이게 된다
            bodyHtml: `<p class="ma-mdl-desc"><b>${this.esc(p.chargeTitle)}</b> — ${this.esc(p.punishTitle)}</p>
                <p class="ma-mdl-help">행은 지워지지 않고 <b>해제 이력</b>으로 남습니다.</p>
                <label class="ma-mdl-lb">해제 사유</label>
                <textarea class="ma-mdl-ta" id="maPenMemo" maxlength="500">관리자 해제</textarea>`,
            onOk: (b) => {
                const memo = (b.querySelector('#maPenMemo').value || '').trim();
                if (!memo) return this.toast('해제 사유를 입력해주세요');

                return this.save('release', { num: p.num, memo });
            },
        });
    }

    /** 해제된 제재 삭제 — 해제와 달리 **행이 없어진다.** 되돌릴 수 없으니 무엇을 지우는지 먼저 보여준다 */
    deleteForm(p) {
        if (!p) return this.toast('삭제할 제재를 찾을 수 없습니다');

        this.formModal({
            title: '제재 삭제', okLabel: '삭제', okCls: 'danger',
            bodyHtml: `<p class="ma-mdl-desc"><b>${this.esc(p.chargeTitle)}</b> — ${this.esc(p.punishTitle)}</p>
                <p class="ma-mdl-help">이 줄을 원장에서 <b>지웁니다</b>. 해제 이력도 함께 사라지며 되돌릴 수 없습니다.</p>`,
            onOk: () => this.save('delete', { num: p.num }),
        });
    }

    // ───────── 통신 ─────────

    /**
     * 부과·수정·해제·삭제 공통.
     *
     * 성공해도 `close()` 를 부르지 않는다 — 목록을 다시 그리면 그 모달이 지금 창을 갈아끼우고
     * (stacked) 히스토리 항목도 물려받는다. close() 를 먼저 부르면 그 안의 `history.back()` 이
     * 비동기로 돌면서 방금 연 목록까지 닫는다 (Sites/MobileAdmin.modal 주석).
     */
    save(seg, data) {
        $.post(`/ma/ajax/member/${this.mbNo}/penalty/${seg}`, data)
            .done((res) => {
                const r = (res && typeof res === 'object') ? res : {};
                if (!r.result) return this.toast(r.message || '처리하지 못했습니다');

                this.merge(r);
                this.toast(r.message || '처리했습니다');
                this.openList();
                if (this.onChange) this.onChange();   // 기사 상세의 제재 요약·딱지도 다시 받는다
            })
            .fail((xhr) => this.toast(xhr.status === 401 ? '세션이 만료되었습니다' : '처리에 실패했습니다'));
    }

    // ───────── 도우미 ─────────

    /**
     * 입력창 — [취소]는 창을 닫지 않고 **목록으로 되돌린다.**
     * 닫아버리면 해제 하나 취소했다고 보던 목록까지 사라진다.
     */
    formModal(opts) {
        const m    = this.M.modal(opts);
        const back = m.bodyEl.closest('.ma-modal-back');
        const no   = back && back.querySelector('.ma-modal-cancel');
        if (no) no.onclick = () => this.openList();

        return m;
    }

    /** 제재는 소유자에게만 — 소속자 화면은 읽기전용이다 (서버도 denySosok 으로 막는다) */
    isSosok() { return !!((this.sanc || {}).owner || {}).isSosok; }

    ownerLabel() {
        const o = ((this.sanc || {}).owner) || {};

        return `${o.ownerName || ''} #${this.n(o.ownerNo)}`.trim();
    }

    rowOf(num)      { return ((this.sanc || {}).list || []).find((r) => +r.num === +num) || null; }
    rowOfCase(num)  { return ((this.sanc || {}).list || []).find((r) => +r.caseNum === +num) || null; }

    toast(msg) { if ($j.Ui && $j.Ui.Toast) $j.Ui.Toast.show(msg); }
    n(v) { return parseInt(v, 10) || 0; }
    esc(s) {
        return String(s == null ? '' : s)
            .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    }
}

/**
 * 기사 상세에서 부르는 입구.
 * @param {object} o mbNo · name · sanc · onChange (PenaltySheet 참고)
 */
export function openPenalty(o) {
    new PenaltySheet(o).open();
}
